import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { API_BASE_URL, getHeaders } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';
import { showToast } from '../utils/toast';
import OrgStorageManagement from './shared/OrgStorageManagement';
import {
  FaBuilding,
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaCheck,
  FaChevronDown,
  FaSyncAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaHashtag,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCircle,
  FaCheckCircle,
  FaToggleOn,
  FaToggleOff,
  FaCubes,
  FaBan,
  FaCog
} from "react-icons/fa";

export default function Organizations({ token, roles = [] }) {
  // Data State
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);

  // UI & UX State
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; 
  const formRef = useRef(null);
  const tableRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    description: '',
    address: '',
    isActive: true,
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    employeeId: '',
    initialLicensingTierKey: 'free'
  });

  const [editingOrgId, setEditingOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [formConfirm, setFormConfirm] = useState(false);
  const [blockingState, setBlockingState] = useState(null);

  const isSystemAdmin = Array.isArray(roles) && roles.includes("SystemAdministrator");
  const isOrgAdmin = Array.isArray(roles) && (roles.includes("OrganizationAdministrator") || roles.includes("OrgAdmin") || isSystemAdmin);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    setBlockingState(null);
    try {
      const res = await fetch(`${API_BASE_URL}/organizations`, { headers: getHeaders(token) });

      if (!res.ok) {
        throw new Error(await readAdminApiErrorMessage(res, {
          401: 'Your session is invalid or expired. Please sign in again.',
          403: 'You do not have permission to view organizations.',
          500: 'Failed to load organizations.',
          default: 'Failed to load organizations.'
        }));
      }

      const data = await res.json();
      setOrgs(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast.error(err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentOrg = useCallback(async () => {
    try {
      const payloadBase64 = token.split('.')[1];
      const decoded = JSON.parse(atob(payloadBase64));
      const orgId = decoded.org_id;
      
      if (!orgId) {
        setCurrentOrg(null);
        setBlockingState({
          title: 'Organization context unavailable',
          message: 'Your organization context is unavailable. Please sign in again.'
        });
        return;
      }

      setLoading(true);
      setBlockingState(null);
      const res = await fetch(`${API_BASE_URL}/organizations/${orgId}`, { headers: getHeaders(token) });

      if (!res.ok) {
        const message = await readAdminApiErrorMessage(res, {
          400: 'Invalid organization context',
          401: 'Your session is invalid or expired. Please sign in again.',
          403: 'You do not have permission to view organization details.',
          404: `Organization with identifier ${orgId} not found.`,
          500: 'Failed to load organization details.',
          default: 'Failed to load organization details.'
        });

        setCurrentOrg(null);
        setBlockingState({
          title: res.status === 401
            ? 'Session expired'
            : res.status === 403
              ? 'Access denied'
              : res.status === 404
                ? 'Organization not found'
                : 'Organization unavailable',
          message
        });
        return;
      }

      const data = await res.json();
      setCurrentOrg(data);
      setBlockingState(null);
    } catch (err) {
      console.error('Error fetching organization:', err);
      setCurrentOrg(null);
      setBlockingState({
        title: 'Organization unavailable',
        message: 'Failed to load organization details.'
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      if (isSystemAdmin) {
        fetchOrgs();
      } else {
        // Non-system users read only their own organization details.
        fetchCurrentOrg();
      }
    }
  }, [token, isSystemAdmin, fetchOrgs, fetchCurrentOrg]);

  const executeAction = async () => {
    if (!confirmation) return;
    const { id, type, data } = confirmation;

    if (type === 'EDIT') {
      setEditingOrgId(id);
      setFormData({
        name: data.name || '',
        reference: data.reference || '',
        description: data.description || '',
        address: data.address || '',
        isActive: data.isActive,
        initialLicensingTierKey: data.initialLicensingTierKey || '',
        firstName: '', lastName: '', email: '', username: '', password: '', employeeId: ''
      });
      setShowForm(true);
      setConfirmation(null);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      return;
    }

    setSubmitting(true);
    setConfirmation(null);

    try {
      if (type === 'DELETE') {
        const res = await fetch(`${API_BASE_URL}/organizations/${id}`, {
          method: 'DELETE', headers: getHeaders(token)
        });

        if (!res.ok) {
          throw new Error(await readAdminApiErrorMessage(res, {
            401: 'Your session is invalid or expired. Please sign in again.',
            403: 'You do not have permission to delete organizations.',
            404: `Organization with identifier ${id} not found.`,
            409: 'Organization cannot be deleted because it still has dependent records or is protected.',
            500: 'Organization deletion failed. Please try again.',
            default: 'Organization deletion failed. Please try again.'
          }));
        }

        showToast.success('Organization deleted successfully');
      } else {
        const newStatus = type === 'ACTIVATE';
        const res = await fetch(`${API_BASE_URL}/organizations/${id}`, {
          method: 'PUT',
          headers: { ...getHeaders(token), 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...data, isActive: newStatus })
        });

        if (!res.ok) {
          throw new Error(await readAdminApiErrorMessage(res, {
            401: 'Your session is invalid or expired. Please sign in again.',
            403: 'You do not have permission to update organizations.',
            404: `Organization with ID ${id} not found.`,
            500: 'Organization update failed. Please try again.',
            default: 'Organization update failed. Please try again.'
          }));
        }

        showToast.success(`Organization ${newStatus ? 'activated' : 'deactivated'} successfully`);
      }
      fetchOrgs();
    } catch (err) {
      showToast.error(err.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRequest = (e, org) => {
    e.stopPropagation();
    setConfirmation({ id: org.id, type: 'EDIT', data: org });
  };

  const parseOrganizationSaveError = useCallback((res, isEditing) => {
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: isEditing
        ? 'You do not have permission to update organizations.'
        : 'You do not have permission to create organizations.',
      404: 'The organization no longer exists.',
      409: 'Conflict: Organization name, website, admin email, username, or employee ID already exists.',
      500: 'Something went wrong on our side. Please try again shortly.',
      default: 'The server encountered an issue saving this organization.'
    });
  }, []);

  const handleFinalSubmit = async (e) => {
    if (e) e.preventDefault();
    setFormConfirm(false);
    setSubmitting(true);

    try {
      const headers = { ...getHeaders(token), 'Content-Type': 'application/json' };
      let res;

      if (editingOrgId) {
        res = await fetch(`${API_BASE_URL}/organizations/${editingOrgId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            name: formData.name,
            reference: formData.reference,
            description: formData.description,
            address: formData.address,
            isActive: formData.isActive,
            initialLicensingTierKey: formData.initialLicensingTierKey
          })
        });
      } else {        
        const payload = {
          organization: { 
            name: formData.name, 
            reference: formData.reference, 
            description: formData.description, 
            address: formData.address, 
            isActive: formData.isActive,            
          },
          adminUser: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            username: formData.username,
            password: formData.password,
            employeeId: formData.employeeId
          },
          initialLicensingTierKey: formData.initialLicensingTierKey
        };
        // console.log('Submitting organization with payload:', payload);
        res = await fetch(`${API_BASE_URL}/organizations`, {
          method: 'POST', headers, body: JSON.stringify(payload)
        });
      }
      
      if (!res.ok) {
        const normalizedMessage = await parseOrganizationSaveError(res, Boolean(editingOrgId));
        throw new Error(normalizedMessage);
      }

      showToast.success(editingOrgId ? 'Organization updated successfully' : 'Organization onboarded successfully');
      setShowForm(false);
      setEditingOrgId(null);

      setFormData({
        name: '', reference: '', description: '', address: '', isActive: true,
        firstName: '', lastName: '', email: '', username: '', password: '', employeeId: '',
        initialLicensingTierKey: 'free'
      });

      fetchOrgs();
    } catch (err) {
      console.error('Error saving organization:', err);
      showToast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrgs = useMemo(() => {
    return orgs.filter(o => {
      const matchesSearch = (o.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o.reference || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && o.isActive) ||
        (filterStatus === 'inactive' && !o.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [orgs, searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredOrgs.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredOrgs.slice(start, start + itemsPerPage);
  }, [filteredOrgs, currentPage]);

  const stats = {
    total: orgs.length,
    active: orgs.filter(o => o.isActive).length,
    inactive: orgs.filter(o => !o.isActive).length
  };

  const handleKpiClick = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  return (
    <div style={wrapperStyle}>
      <div style={containerStyle}>

        <header style={headerStyle}>
          <div style={headerContentStyle}>
            <div style={headerBadgeStyle}><FaBuilding size={22} /></div>
            <div>
              <h1 style={titleStyle}>Organization {isSystemAdmin ? 'Registry' : 'Details'}</h1>
              <p style={subtitleStyle}>
                {isSystemAdmin 
                  ? `Managing ${stats.total} corporate entities and root directories.`
                  : 'View your organization information'
                }
              </p>
            </div>
          </div>
        </header>

        {isSystemAdmin && (
          <>
            <div style={kpiRowStyle}>
              <div className="kpi-card" style={filterStatus === 'all' ? kpiCardActiveAll : kpiCardWhite} onClick={() => handleKpiClick('all')}>
                <div style={kpiHeader}>
                  <div style={{...kpiIcon, color: '#6366f1', background: '#f5f3ff'}}><FaCubes /></div>
                  <span style={kpiLabel}>Total Entities</span>
                </div>
                <div style={kpiValueStyle}>{stats.total}</div>
                <div style={kpiMetaStyle}>Approved organizations</div>
              </div>
              
              <div className="kpi-card" style={filterStatus === 'active' ? kpiCardActiveActive : kpiCardWhite} onClick={() => handleKpiClick('active')}>
                <div style={kpiHeader}>
                  <div style={{ ...kpiIcon, background: '#f0fdf4', color: '#16a34a' }}><FaCheckCircle /></div>
                  <span style={kpiLabel}>Active</span>
                </div>
                <div style={{ ...kpiValueStyle, color: '#10b981' }}>{stats.active}</div>
                <div style={kpiMetaStyle}>Active organizations</div>
              </div>

              <div className="kpi-card" style={filterStatus === 'inactive' ? kpiCardActiveInactive : kpiCardWhite} onClick={() => handleKpiClick('inactive')}>
                <div style={kpiHeader}>
                  <div style={{ ...kpiIcon, background: '#fef2f2', color: '#dc2626' }}><FaBan /></div>
                  <span style={kpiLabel}>Disabled</span>
                </div>
                <div style={{ ...kpiValueStyle, color: '#ef4444' }}>{stats.inactive}</div>
                <div style={kpiMetaStyle}>Disabled organizations</div>
              </div>
            </div>

            <div style={{ ...filtersCardStyle, marginBottom: 24 }}>
              <div style={cardHeaderStyle}>
                <FaCog color="#6366f1" size={14} />
                <span style={cardTitleStyle}>Registry Controls</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={searchWrapper}>
                  <FaSearch style={searchIcon} />
                  <input 
                    style={searchInput} 
                    placeholder="Search directory by name or website..." 
                    value={searchTerm} 
                    onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} 
                  />
                </div>
                <div>
                <button
                  onClick={() => { 
                    setEditingOrgId(null); 
                    setShowForm(!showForm); 
                    setFormConfirm(false);
                    setFormData({
                      name: '', reference: '', description: '', address: '', isActive: true,
                      firstName: '', lastName: '', email: '', username: '', password: '', employeeId: '',
                      initialLicensingTierKey: 'free'
                    });
                  }}
                  style={primaryButtonStyle}
                >
                  {showForm ? <FaTimes /> : <FaPlus />} {showForm ? 'Close Editor' : 'New Organization'}
                </button>
                </div>
              </div>
            </div>

            {showForm && (
              <div ref={formRef} style={{ ...filtersCardStyle, border: '1px solid #6366f1', animation: 'fadeIn 0.4s ease-out', marginBottom: 24 }}>
                <div style={cardHeaderStyle}>
                  <FaBuilding color="#6366f1" size={14} />
                  <span style={cardTitleStyle}>{editingOrgId ? 'Edit Entity Details' : 'Onboard New Organization'}</span>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setFormConfirm(true); }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <label style={labelStyle}>Basic Configuration</label>
                      
                      <div>
                        <div style={fieldHintStyle}>Organization Name</div>
                        <input style={selectStyle} placeholder="E.g., Acme Corporation" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                      </div>

                      <div>
                        <div style={fieldHintStyle}>Website</div>
                        <input style={{ ...selectStyle, fontFamily: 'monospace' }} placeholder="E.g., www.acme.com" value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} />
                      </div>

                      <div>
                        <div style={fieldHintStyle}>Description</div>
                        <textarea style={{ ...selectStyle, height: 70, resize: 'none' }} placeholder="Brief entity purpose..." value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
                      </div>

                      <div>
                        <div style={fieldHintStyle}>Physical Address</div>
                        <textarea style={{ ...selectStyle, height: 70, resize: 'none' }} placeholder="Full street address..." value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required />
                      </div>
                      {!editingOrgId ? (
                        <div>
                          <div style={fieldHintStyle}>Initial Licensing Tier Key</div>
                          <input type="text" style={selectStyle} value={formData.initialLicensingTierKey} onChange={(e) => setFormData({ ...formData, initialLicensingTierKey: e.target.value })} />
                        </div>                       
                      ): null}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {!editingOrgId ? (
                        <>
                          <label style={labelStyle}>Primary Administrator</label>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <input style={selectStyle} placeholder="First Name" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} required />
                            <input style={selectStyle} placeholder="Last Name" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} required />
                          </div>

                          <input style={selectStyle} type="email" placeholder="Email Address" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                          <input style={selectStyle} placeholder="System Username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required />
                          <input style={selectStyle} type="password" placeholder="Password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
                          <input style={selectStyle} placeholder="Internal Employee ID" value={formData.employeeId} onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })} required />
                        </>
                      ) : null}

                      <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                        {formConfirm ? (
                          <div style={inlineConfirmWrapper}>
                            <span style={confirmLabel}>Save changes?</span>
                            <button type="button" style={btnYes} onClick={handleFinalSubmit} disabled={submitting}>
                              {submitting ? <FaSyncAlt className="spin" /> : 'Confirm'}
                            </button>
                            <button type="button" style={btnNo} onClick={() => setFormConfirm(false)}>Discard</button>
                          </div>
                        ) : (
                          <>
                            <button type="button" onClick={() => setShowForm(false)} style={{ ...btnNo, padding: '10px 20px' }}>Discard</button>
                            <button type="submit" disabled={submitting} style={primaryButtonStyle}>
                              {submitting ? <FaSyncAlt className="spin" /> : <FaCheck />} {editingOrgId ? 'Update Entity' : 'Finalize Registry'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        <div ref={tableRef} style={tableCardStyle}>
          {loading ? (
            <div style={emptyStateStyle}><FaSyncAlt className="spin" /> Loading organization data...</div>
          ) : isSystemAdmin ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={tableHeaderRowStyle}>
                    <th style={thStyle}>Organization Information</th>
                    <th style={thStyle}>Status</th>
                    <th style={thActionStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr><td colSpan="3" style={emptyStateStyle}>No organizations found matching criteria.</td></tr>
                  ) : (
                    currentItems.map((org) => {
                      const isExpanded = expandedId === org.id;
                      return (
                        <React.Fragment key={org.id}>
                          <tr style={trStyle} onClick={() => setExpandedId(isExpanded ? null : org.id)} className="clickable-row">
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={avatarStyle}><FaBuilding size={16} /></div>
                                <div>
                                  <div style={userNameText}>{org.name}</div>
                                  <div style={userEmailText}><FaHashtag size={10} /> {org.reference || 'Website not provided'}</div>
                                </div>
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <span style={getBadgeStyle(org.isActive)}>
                                <FaCircle size={6} /> {org.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={tdActionStyle}>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                                {confirmation?.id === org.id ? (
                                  <div style={inlineConfirmWrapper} onClick={e => e.stopPropagation()}>
                                    <span style={confirmText}>{confirmation.type}?</span>
                                    <button style={btnYes} onClick={executeAction} disabled={submitting}>
                                      {submitting ? <FaSyncAlt className="spin" size={10} /> : 'Yes'}
                                    </button>
                                    <button style={btnNo} onClick={() => setConfirmation(null)}>No</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <button style={viewDetailsBtnStyle} onClick={(e) => handleEditRequest(e, org)} title="Edit Configuration">
                                      <FaEdit size={12} />
                                    </button>
                                    <button
                                      style={org.isActive ? actionBtnReject : actionBtnApprove}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmation({ id: org.id, type: org.isActive ? 'DEACTIVATE' : 'ACTIVATE', data: org });
                                      }}
                                      title={org.isActive ? "Revoke Access" : "Grant Access"}
                                    >
                                      {org.isActive ? <FaToggleOn size={14} /> : <FaToggleOff size={14} />}
                                    </button>
                                    <button style={revokeBtn} onClick={(e) => { e.stopPropagation(); setConfirmation({ id: org.id, type: 'DELETE' }); }} title="Remove Entity">
                                      <FaTrash size={12} />
                                    </button>
                                    <div style={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                                      <FaChevronDown style={{ color: '#94a3b8', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="3" style={{ padding: 0, border: 'none' }}>
                                <div style={expandedBoxStyle}>
                                  <div style={expandedGridStyle}>
                                    <div style={{ flex: 1 }}>
                                      <div style={detailSectionLabel}><FaInfoCircle style={{ marginRight: 6 }} /> Registry Details</div>
                                      <p style={detailText}>{org.description || 'No detailed description registered for this entity.'}</p>
                                      <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
                                        <FaMapMarkerAlt /> {org.address || 'Physical address not recorded.'}
                                      </div>
                                    </div>
                                    <div style={{ flex: 1, borderLeft: '1px solid #e2e8f0', paddingLeft: 32 }}>
                                      <div style={detailSectionLabel}><FaSyncAlt style={{ marginRight: 6 }} /> System Metadata</div>
                                      <div style={metaDataCard}>
                                        <div style={metaItem}><span style={metaLabel}>Internal ID:</span> {org.id}</div>
                                        <div style={metaItem}><span style={metaLabel}>Website:</span> {org.reference || 'Not provided'}</div>
                                        <div style={metaItem}><span style={metaLabel}>Status:</span> {org.isActive ? 'Active' : 'Suspended'}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <OrgStorageManagement organizationId={org.id} canManage={isOrgAdmin || isSystemAdmin} />
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : blockingState ? (
            <div style={{ padding: '60px 40px', animation: 'fadeIn 0.6s ease' }}>
              <div style={{ maxWidth: 720, margin: '0 auto', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 16, padding: 32 }}>
                <div style={{ ...detailSectionLabel, color: '#9a3412', marginBottom: 16 }}>{blockingState.title}</div>
                <p style={{ ...detailText, color: '#9a3412' }}>{blockingState.message}</p>
              </div>
            </div>
          ) : (
            currentOrg && (
              <div style={{ padding: '60px 40px', animation: 'fadeIn 0.6s ease' }}>
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                  <div style={{ textAlign: 'center', marginBottom: 40 }}>
                    <div style={{ ...headerBadgeStyle, margin: '0 auto 24px', width: 64, height: 64, background: '#f5f3ff' }}>
                      <FaBuilding size={32} />
                    </div>
                    <h2 style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>{currentOrg.name}</h2>
                    <div style={{ ...typeBadgeStyle, margin: '0 auto' }}>
                      <FaHashtag size={10} style={{ marginRight: 6 }} /> {currentOrg.reference || 'Website not provided'}
                    </div>
                  </div>

                  <div style={{ background: '#f8fafc', borderRadius: 16, padding: 32, marginBottom: 24 }}>
                    <div style={detailSectionLabel}><FaInfoCircle style={{ marginRight: 6 }} /> Organization Details</div>
                    <p style={{ ...detailText, marginTop: 12, marginBottom: 20 }}>
                      {currentOrg.description || 'No description available'}
                    </p>
                    
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, color: '#64748b', fontSize: 14 }}>
                      <FaMapMarkerAlt style={{ marginTop: 3, flexShrink: 0 }} />
                      <span>{currentOrg.address || 'Physical address not available'}</span>
                    </div>
                  </div>

                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24 }}>
                    <div style={detailSectionLabel}><FaSyncAlt style={{ marginRight: 6 }} /> System Information</div>
                    <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
                      <div style={metaItem}>
                        <span style={metaLabel}>Organization ID:</span> 
                        <span>{currentOrg.id}</span>
                      </div>
                      <div style={metaItem}>
                        <span style={metaLabel}>Website:</span> 
                        <span>{currentOrg.reference || 'Not provided'}</span>
                      </div>
                      <div style={metaItem}>
                        <span style={metaLabel}>Status:</span> 
                        <span style={getBadgeStyle(currentOrg.isActive)}>
                          <FaCircle size={6} /> {currentOrg.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <OrgStorageManagement organizationId={currentOrg.id} canManage={isOrgAdmin || isSystemAdmin} />
                </div>
              </div>
            )
          )}

          {isSystemAdmin && totalPages > 1 && (
            <div style={paginationRow}>
              <div style={paginationInfoStyle}>Showing <b>{currentItems.length}</b> of <b>{filteredOrgs.length}</b> entities</div>
              <div style={paginationControlsWrapper}>
                <button style={pageArrow} disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}><FaChevronLeft /></button>
                <span style={{ margin: '0 12px', fontSize: 14, color: '#64748b' }}>Page {currentPage} of {totalPages}</span>
                <button style={pageArrow} disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}><FaChevronRight /></button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .spin { animation: spin 1s infinite linear; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .kpi-card { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; overflow: hidden; cursor: pointer; }
        .kpi-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1) !important; }
        .clickable-row { cursor: pointer; transition: background 0.2s; }
        .clickable-row:hover { background: #f8fafc !important; }
      `}</style>
    </div>
  );
}

/* ---------- STYLES ---------- */
const wrapperStyle = { width: '100%', minHeight: '100vh', background: '#f8fafc', paddingBottom: 60, fontFamily: 'Inter, sans-serif' };
const containerStyle = { maxWidth: 1150, margin: '0 auto', padding: '24px', animation: 'fadeIn 0.5s ease-out' };
const headerStyle = { marginBottom: 32 };
const headerContentStyle = { display: 'flex', alignItems: 'center', gap: 16 };
const headerBadgeStyle = { width: 48, height: 48, borderRadius: 14, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.1)' };
const titleStyle = { fontSize: 24, fontWeight: 800, margin: 0, color: '#0f172a' };
const subtitleStyle = { fontSize: 14, color: '#64748b', marginTop: 4 };

const kpiRowStyle = { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 32 };
const kpiCardBase = { padding: 24, borderRadius: 24, transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', border: '1px solid #e2e8f0', background: '#fff' };
const kpiCardWhite = { ...kpiCardBase, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' };
const kpiCardActiveAll = { ...kpiCardBase, border: '2px solid #6366f1', background: '#f5f3ff', boxShadow: '0 10px 15px -3px rgba(99, 102, 241, 0.1)' };
const kpiCardActiveActive = { ...kpiCardBase, border: '2px solid #10b981', background: '#f0fdf4', boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.1)' };
const kpiCardActiveInactive = { ...kpiCardBase, border: '2px solid #ef4444', background: '#fef2f2', boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.1)' };
const kpiHeader = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 };
const kpiIcon = { width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const kpiLabel = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' };
const kpiValueStyle = { fontSize: 32, fontWeight: 800, color: '#1e293b' };
const kpiMetaStyle = { fontSize: 11, color: '#94a3b8' };

const filtersCardStyle = { background: '#fff', borderRadius: 16, padding: 24, border: '1px solid #e2e8f0' };
const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid #f1f5f9' };
const cardTitleStyle = { fontSize: 15, fontWeight: 700, color: '#1e293b' };
const labelStyle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' };
const fieldHintStyle = { fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: '6px' };
const selectStyle = { width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 14, color: '#1e293b', background: '#fff', outline: 'none' };
const primaryButtonStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', fontSize: 14 };

const tableCardStyle = { background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' };
const searchWrapper = { position: 'relative', width: '320px' };
const searchIcon = { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };

const searchInput = { 
  width: '100%', 
  padding: '9px 12px 9px 38px', 
  borderRadius: 10, 
  border: '1.5px solid #e2e8f0', 
  fontSize: 13, 
  outline: 'none',
  background: '#fff',
  color: '#1e293b'
};

const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const tableHeaderRowStyle = { background: '#f8fafc' };
const thStyle = { padding: '16px 20px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', textAlign: 'left' };
const thActionStyle = { ...thStyle, textAlign: 'right' };
const trStyle = { borderBottom: '1px solid #f1f5f9' };
const tdStyle = { padding: '16px 20px', verticalAlign: 'middle' };
const tdActionStyle = { padding: '16px 20px', textAlign: 'right' };
const avatarStyle = { width: 38, height: 38, borderRadius: 10, background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 };
const userNameText = { fontWeight: 700, color: '#1e293b', fontSize: 14 };
const userEmailText = { fontSize: 12, color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 };
const typeBadgeStyle = { background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, display: 'inline-flex', alignItems: 'center' };

const actionBtnBase = { padding: '8px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', border: 'none' };
const viewDetailsBtnStyle = { ...actionBtnBase, background: '#e0f2fe', border: '1px solid #bae6fd', color: '#0369a1' };
const actionBtnApprove = { ...actionBtnBase, background: '#dcfce7', border: '1px solid #bbf7d0', color: '#16a34a' };
const actionBtnReject = { ...actionBtnBase, background: '#fee2e2', border: '1px solid #fecaca', color: '#dc2626' };
const revokeBtn = { ...actionBtnBase, background: '#fff', border: '1.5px solid #e2e8f0', color: '#64748b' };

const expandedBoxStyle = { padding: '28px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', animation: 'fadeIn 0.3s' };
const expandedGridStyle = { display: 'flex', gap: '40px' };
const detailSectionLabel = { fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', display: 'flex', alignItems: 'center' };
const detailText = { fontSize: '14px', color: '#334155', lineHeight: '1.6', margin: 0 };
const metaDataCard = { background: '#fff', padding: 18, borderRadius: 16, border: '1px solid #e2e8f0' };
const metaItem = { fontSize: 13, marginBottom: 8, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 };
const metaLabel = { fontWeight: 700, color: '#94a3b8' };

const inlineConfirmWrapper = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '6px 12px', borderRadius: 10, border: '1px solid #e2e8f0' };
const confirmLabel = { fontWeight: 700, fontSize: 13, color: '#1e293b' };
const confirmText = { fontSize: '12px', fontWeight: '700', color: '#475569' };
const btnYes = { background: '#6366f1', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 };
const btnNo = { background: '#fff', color: '#64748b', border: '1.5px solid #e2e8f0', padding: '6px 14px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' };

const emptyStateStyle = { padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 };

const paginationRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#fff', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 16 };
const paginationInfoStyle = { fontSize: 14, color: '#64748b', fontWeight: 500 };
const paginationControlsWrapper = { display: 'flex', alignItems: 'center', gap: 8 };
const pageArrow = { minWidth: 36, height: 36, padding: '0 4px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', fontSize: 13, fontWeight: '700', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' };

const getBadgeStyle = (isActive) => {
  const base = { padding: '4px 10px', borderRadius: '20px', fontSize: '10px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, textTransform: 'uppercase' };
  return isActive ? { ...base, background: '#dcfce7', color: '#166534' } : { ...base, background: '#fee2e2', color: '#991b1b' };
};